import { useState } from 'react'

import { errorMessage } from '@/shared/components/data'
import { Button, Field, Input, Select, Sheet } from '@/shared/components/ui'
import { useUserIndex } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import type { ProjectDetail } from '@/shared/types/domain'

import { useEditProject } from '../api'

export interface ProjectEditFormProps {
  project: ProjectDetail
  onClose: () => void
}

/**
 * Editar la ficha del proyecto.
 *
 * Formulario **propio** y no el de alta con un modo más, al revés de lo que se
 * hizo con las empresas. El de alta y éste no comparten campos: aquel pide
 * empresa y plantilla de arquitectura —dos cosas que el `PATCH` no acepta,
 * porque mover un proyecto de empresa rompería la FK compuesta y la
 * arquitectura ya está creada— y éste ofrece semáforo y contacto, que en el
 * alta no existen todavía. Un solo componente con la mitad de los campos
 * apagados según el modo es más difícil de leer que dos.
 *
 * Tres cosas siguen fuera, y no por olvido:
 *
 * * **La fase** se cambia con un clic en el pill de la barra, que es donde se
 *   mira. Traerla acá la escondería detrás de un formulario.
 * * **La visibilidad al cliente** tiene su endpoint y su interruptor: es la
 *   decisión más delicada de la app y queda auditada como acto explícito.
 * * **La prioridad** la decide el orden global, en `/priorities`.
 */
export function ProjectEditForm({ project, onClose }: ProjectEditFormProps) {
  const t = useT()
  const edit = useEditProject(project.id)
  const users = useUserIndex()

  const [name, setName] = useState(project.name)
  const [ownerId, setOwnerId] = useState(project.internal_owner_id ?? '')
  const [startDate, setStartDate] = useState(project.start_date ?? '')
  const [targetEnd, setTargetEnd] = useState(project.target_end_date ?? '')
  const [health, setHealth] = useState(project.health ?? '')
  const [brief, setBrief] = useState(project.brief_md ?? '')

  const valid = name.trim().length > 0

  const submit = () => {
    edit.mutate(
      {
        name: name.trim(),
        internal_owner_id: ownerId || null,
        // Las fechas vacías viajan como `null`: borrar la entrega objetivo es
        // una decisión posible, y un `''` haría fallar la validación del
        // backend con un mensaje sobre el formato de fecha.
        start_date: startDate || null,
        target_end_date: targetEnd || null,
        health: (health || null) as 'green' | 'yellow' | 'red' | null,
        brief_md: brief,
      },
      { onSuccess: () => onClose() },
    )
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('projects.form.editTitle')}</h2>}
      footer={
        <div className="flex items-center justify-between gap-2">
          {edit.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(edit.error, t('projects.error.update'))}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || edit.isPending} onClick={submit}>
              {edit.isPending ? t('common.state.saving') : t('common.action.save')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        <Field label={t('common.label.name')} htmlFor="project-name">
          <Input
            id="project-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>

        <Field label={t('common.label.owner')} htmlFor="project-owner">
          <Select
            id="project-owner"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
          >
            <option value="">{t('common.label.noOwner')}</option>
            {[...users.values()].map((user) => (
              <option key={user.id} value={user.id}>
                {user.display_name}
              </option>
            ))}
          </Select>
        </Field>

        {/* El semáforo no tenía dónde ponerse: la columna existe en el
            portafolio y el valor sólo se podía cambiar por API. */}
        <Field label={t('projects.field.health')} htmlFor="project-health">
          <Select
            id="project-health"
            value={health}
            onChange={(event) => setHealth(event.target.value)}
          >
            <option value="">{t('common.health.unknown')}</option>
            <option value="green">{t('common.health.green')}</option>
            <option value="yellow">{t('common.health.yellow')}</option>
            <option value="red">{t('common.health.red')}</option>
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('projects.summary.start')} htmlFor="project-start">
            <Input
              id="project-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </Field>

          <Field
            label={t('projects.summary.targetEnd')}
            htmlFor="project-target"
            hint={project.is_ongoing ? t('projects.form.ongoingHint') : undefined}
          >
            <Input
              id="project-target"
              type="date"
              value={targetEnd}
              disabled={project.is_ongoing}
              onChange={(event) => setTargetEnd(event.target.value)}
            />
          </Field>
        </div>

        <Field label={t('projects.summary.brief')} htmlFor="project-brief">
          <textarea
            id="project-brief"
            rows={8}
            value={brief}
            onChange={(event) => setBrief(event.target.value)}
            className="hairline w-full rounded-[var(--radius-control)] bg-surface-2 px-2 py-1.5 text-xs"
          />
        </Field>
      </form>
    </Sheet>
  )
}
